import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Boxes, PencilRuler, HardHat, Wallet, ShieldCheck, Network, Sparkles, Play } from "lucide-react";
import { MarketingLayout, Section } from "@/components/marketing/MarketingLayout";
import { IMG, inr } from "@/lib/constants";
import { Button } from "@/components/ui/button";

const fadeUp = { initial: { opacity: 0, y: 28 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

const PILLARS = [
  { icon: Boxes, t: "Plan", d: "Site analysis, feasibility and planning" },
  { icon: PencilRuler, t: "Design", d: "Floor plans, 3D, elevation, drawings" },
  { icon: HardHat, t: "Build", d: "Project management, tasks, progress" },
  { icon: Wallet, t: "Manage", d: "BOQ, budget, procurement, vendors" },
  { icon: ShieldCheck, t: "Inspect", d: "Quality, inspections, snagging" },
  { icon: Network, t: "Connect", d: "Clients, contractors, architects" },
  { icon: Sparkles, t: "Intelligence", d: "AI predictions & insights" },
];

const STATS = [["₹4,200 Cr+", "Projects Managed"], ["18,500+", "Verified Contractors"], ["99.4%", "On-Time Milestones"], ["28", "Cities"]];

export default function Home() {
  const nav = useNavigate();
  return (
    <MarketingLayout>
      {/* HERO */}
      <section className="relative bg-sp-navy text-white overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={IMG.heroWorkers} alt="Indian construction site" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-sp-navy via-sp-navy/90 to-sp-navy/40" />
        </div>
        <div className="relative sp-container py-28 lg:py-40 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <motion.div {...fadeUp} className="sp-eyebrow mb-5">The Operating System for Construction</motion.div>
            <motion.h1 {...fadeUp} transition={{ duration: 0.7, delay: 0.05 }}
                       className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight leading-[1.05]">
              Build better.<br />Build with <span className="text-sp-orange">Sitepar.</span>
            </motion.h1>
            <motion.p {...fadeUp} transition={{ duration: 0.7, delay: 0.12 }}
                      className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
              The digital operating system connecting people, projects, materials and intelligence across construction.
            </motion.p>
            <motion.div {...fadeUp} transition={{ duration: 0.7, delay: 0.18 }} className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-start-project" onClick={() => nav("/register")}
                      className="bg-sp-orange hover:bg-sp-orangedark text-white rounded-full h-12 px-7 text-base">
                Start Your Project <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button data-testid="hero-for-business" variant="outline" onClick={() => nav("/business")}
                      className="rounded-full h-12 px-7 text-base bg-transparent border-white/25 text-white hover:bg-white/10">
                For Businesses
              </Button>
            </motion.div>
          </div>
        </div>
        <div className="relative border-t border-white/10 bg-sp-navy/60">
          <div className="sp-container grid grid-cols-2 md:grid-cols-4">
            {STATS.map(([v, l], i) => (
              <div key={l} className={`py-6 ${i ? "md:border-l border-white/10" : ""}`}>
                <div className="text-2xl lg:text-3xl font-bold text-sp-orange">{v}</div>
                <div className="text-xs text-white/55 mt-1 uppercase tracking-wider">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLATFORM OVERVIEW */}
      <Section>
        <div className="max-w-2xl">
          <div className="sp-eyebrow mb-3">The Platform</div>
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-sp-navy">
            From the first idea to long-term maintenance, in one intelligent platform.
          </h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PILLARS.map((p, i) => (
            <motion.div key={p.t} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.05 }}
                        className="sp-hover-card rounded-lg border border-slate-200 p-6 bg-white">
              <div className="h-11 w-11 rounded-lg bg-sp-navy flex items-center justify-center mb-4">
                <p.icon className="h-5 w-5 text-sp-orange" />
              </div>
              <div className="font-semibold text-sp-navy text-lg">{p.t}</div>
              <div className="text-sm text-slate-500 mt-1 leading-relaxed">{p.d}</div>
            </motion.div>
          ))}
          <Link to="/platform" data-testid="platform-explore-card"
                className="rounded-lg border border-dashed border-sp-orange/40 p-6 flex flex-col justify-center bg-sp-orange/5 hover:bg-sp-orange/10 transition-colors">
            <span className="font-semibold text-sp-orange flex items-center">Explore the platform <ArrowRight className="ml-1 h-4 w-4" /></span>
          </Link>
        </div>
      </Section>

      {/* B2C */}
      <Section dark>
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="sp-eyebrow mb-3">Sitepar Home · B2C</div>
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight">Your home. Your project. Your intelligence.</h2>
            <p className="mt-5 text-white/65 leading-relaxed">
              Plan, design, budget and build your home from one intelligent workspace. Track every rupee,
              every stage and every drawing — with an AI advisor that actually understands your project.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-white/75">
              {["Create a home project in minutes", "Live budget & BOQ tracking", "Documents, drawings & approvals", "AI advisor trained on your project"].map((x) => (
                <li key={x} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-sp-orange" />{x}</li>
              ))}
            </ul>
            <Button data-testid="b2c-start" onClick={() => nav("/register")}
                    className="mt-8 bg-sp-orange hover:bg-sp-orangedark rounded-full h-11 px-6">Start Building My Home</Button>
          </div>
          <div className="relative">
            <img src={IMG.villaDusk} alt="Modern Indian home" className="rounded-xl border border-white/10 w-full object-cover h-[420px]" />
            <div className="absolute -bottom-5 -left-5 sp-glass rounded-xl p-4 w-56 hidden sm:block animate-float">
              <div className="text-xs text-white/55">Rahul Residence · Bengaluru</div>
              <div className="text-2xl font-bold mt-1">42% <span className="text-sm font-normal text-white/60">complete</span></div>
              <div className="h-1.5 bg-white/10 rounded-full mt-2 overflow-hidden"><div className="h-full bg-sp-orange" style={{ width: "42%" }} /></div>
              <div className="text-xs text-white/55 mt-2">{inr(3120000)} / {inr(7200000)}</div>
            </div>
          </div>
        </div>
      </Section>

      {/* B2B */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="order-2 lg:order-1">
            <img src={IMG.cranesSkyline} alt="Construction skyline" className="rounded-xl border border-slate-200 w-full object-cover h-[420px]" />
          </div>
          <div className="order-1 lg:order-2">
            <div className="sp-eyebrow mb-3">Sitepar Business · B2B</div>
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-sp-navy">Run your construction business from one operating system.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Replace fragmented tools with a unified platform for projects, teams, clients, materials and finance.
              Multi-site command center, CRM, estimation, procurement and quality — end to end.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[["Multi-site", "command"], ["CRM", "pipeline"], ["BOQ", "engine"]].map(([a, b]) => (
                <div key={a} className="rounded-lg border border-slate-200 p-4">
                  <div className="font-semibold text-sp-navy">{a}</div><div className="text-xs text-slate-500">{b}</div>
                </div>
              ))}
            </div>
            <Button data-testid="b2b-demo" onClick={() => nav("/business")}
                    className="mt-8 bg-sp-navy hover:bg-sp-charcoal rounded-full h-11 px-6">Explore Sitepar Business</Button>
          </div>
        </div>
      </Section>

      {/* AI */}
      <Section dark className="sp-blueprint">
        <div className="text-center max-w-2xl mx-auto">
          <div className="sp-eyebrow mb-3">Sitepar Intelligence</div>
          <h2 className="text-3xl lg:text-5xl font-bold tracking-tight">Construction has data. Sitepar gives it intelligence.</h2>
          <p className="mt-5 text-white/65">An AI copilot that understands your project, drawings, BOQ, budget and site — grounded in real data, not guesses.</p>
          <Button data-testid="ai-explore" onClick={() => nav("/intelligence")}
                  className="mt-8 bg-sp-orange hover:bg-sp-orangedark rounded-full h-11 px-6">Explore AI</Button>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
          {["Why is my project delayed?", "Compare actual spend with BOQ", "What materials should we order next?"].map((q) => (
            <div key={q} className="sp-glass rounded-lg p-5 text-sm text-white/80">"{q}"</div>
          ))}
        </div>
      </Section>

      {/* Network / India */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="sp-eyebrow mb-3">Sitepar Connect</div>
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-sp-navy">Everyone who builds, connected.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">Homeowners, builders, contractors, architects, engineers, designers, suppliers and vendors — collaborating on the same Project ID, with permission-based visibility.</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Homeowner", "Builder", "Contractor", "Architect", "Engineer", "Designer", "Supplier", "Vendor"].map((r) => (
                <span key={r} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600">{r}</span>
              ))}
            </div>
          </div>
          <div>
            <div className="sp-eyebrow mb-3">Built for India</div>
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-sp-navy">Built for how India builds.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">INR-native BOQ, IS-code aware quality, RERA-ready client portals, and materials priced for the Indian market — from metros to Tier-2 cities.</p>
          </div>
        </div>
      </Section>

      {/* Final CTA */}
      <section className="bg-sp-orange text-white">
        <div className="sp-container py-20 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold tracking-tight">Let's build what's next.</h2>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Button data-testid="cta-start" onClick={() => nav("/register")}
                    className="bg-white text-sp-navy hover:bg-white/90 rounded-full h-12 px-7 text-base">Start Your Project</Button>
            <Button data-testid="cta-business" variant="outline" onClick={() => nav("/business")}
                    className="rounded-full h-12 px-7 text-base bg-transparent border-white/50 text-white hover:bg-white/10">For Business</Button>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
